'use client'

import { useState } from 'react'
import Link from 'next/link'
import { MapPin, Mail, Phone, Twitter, Facebook, Instagram, Linkedin } from 'lucide-react'

// Define the props interface for FooterSection
interface FooterSectionProps {
  openAuthDrawer?: (tab: 'signin' | 'signup') => void;
}

export function FooterSection({ openAuthDrawer }: FooterSectionProps) {
  const openSignUp = () => {
    if (openAuthDrawer) {
      openAuthDrawer('signup');
    }
  }

  return (
    <footer className="bg-[#100D1C] py-12 md:py-16 text-white">
      {/* Add divider here */}
      <div className="container mx-auto px-6">
        <hr className="mb-12 border-t border-gray-700" /> 
      </div>
      
      {/* Main Footer */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
          {/* Company Info */}
          <div>
            <h4 className="mb-4 text-lg font-semibold">NewsClipping</h4>
            <p className="mb-4 text-gray-400">
              Track every mention. Analyze every sentiment. Empower your brand with our cutting-edge news clipping service.
            </p>
            <div className="flex space-x-4">
              <Link href="#" className="text-gray-400 hover:text-[#8A6FE8]">
                <Twitter className="h-5 w-5" />
              </Link>
              <Link href="#" className="text-gray-400 hover:text-[#8A6FE8]">
                <Facebook className="h-5 w-5" />
              </Link>
              <Link href="#" className="text-gray-400 hover:text-[#8A6FE8]">
                <Instagram className="h-5 w-5" />
              </Link>
              <Link href="#" className="text-gray-400 hover:text-[#8A6FE8]">
                <Linkedin className="h-5 w-5" />
              </Link>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="mb-4 text-lg font-semibold">Quick Links</h4>
            <ul className="space-y-2 text-gray-400">
              <li>
                <Link href="/dashboard" className="hover:text-[#8A6FE8]">
                  Dashboard
                </Link>
              </li>
              <li>
                <Link href="/media-clips" className="hover:text-[#8A6FE8]">
                  Media Clips
                </Link>
              </li>
              <li>
                <Link href="/brands" className="hover:text-[#8A6FE8]">
                  Brands
                </Link>
              </li>
              <li>
                <Link href="/reports" className="hover:text-[#8A6FE8]">
                  Reports
                </Link>
              </li>
              <li>
                <Link href="/settings" className="hover:text-[#8A6FE8]">
                  Settings
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="mb-4 text-lg font-semibold">Contact</h4>
            <ul className="space-y-3 text-gray-400">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 shrink-0 text-[#8A6FE8]" />
                <span>123 Media Street, San Francisco, CA 94103</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 shrink-0 text-[#8A6FE8]" />
                <a href="mailto:contact@newsclipping.com" className="hover:text-[#8A6FE8]">
                  contact@newsclipping.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 shrink-0 text-[#8A6FE8]" />
                <a href="tel:+14155552671" className="hover:text-[#8A6FE8]">
                  (415) 555-2671
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="mb-4 text-lg font-semibold">Legal</h4>
            <ul className="space-y-2 text-gray-400">
              <li>
                <Link href="/privacy" className="hover:text-[#8A6FE8]">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link href="#" className="hover:text-[#8A6FE8]">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link href="#" className="hover:text-[#8A6FE8]">
                  Cookie Policy
                </Link>
              </li>
              <li>
                <Link href="#" className="hover:text-[#8A6FE8]">
                  GDPR Compliance
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto flex flex-col items-center justify-between px-6 py-6 md:flex-row">
          <p className="text-sm text-gray-400">
            &copy; {new Date().getFullYear()} NewsClipping. All rights reserved.
          </p>
          <p className="mt-4 text-sm text-gray-400 md:mt-0">
            Made with <span className="text-[#8A6FE8]">♥</span> for brand intelligence
          </p>
        </div>
      </div>
    </footer>
  )
} 